import React, { useContext, useEffect, useState } from 'react'
import { ResponsiveTableRow } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import { getUserCtrData } from '../../../services/userService'
import { UserContext } from '../UserContext'
import { CtrLink } from '../../app/Links'
import { getNumericalIdFromId } from '../../../util/StringUtil'
import useNotifications from '../../../hooks/useNotifications'

const UserCtr = () => {
  usePageView({ page_detail: 'DMConsole:UserCtr' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.ctr'))
  const { error } = useNotifications()
  const { currentUser } = useContext(UserContext)
  const userId = currentUser.contributorId
  const mrn = currentUser.dmcUserMembership.mrn
  const [ctrUserData, setCtrUserData] = useState()

  useEffect(() => {
    const fetchCtrData = async () => {
      const context = mrn ? 'mrn' : null
      const userParam = mrn || userId
      if (userParam !== null) {
        try {
          const response = await getUserCtrData(userParam, context)
          if (response.status === 200) {
            setCtrUserData(() => response.data)
          }
        } catch (err) {
          error(
            `${t('ctr.errors.fetchingCtrs', {
              defaultValue: 'Something went wrong fetching CTRs',
            })}${err.message ? `: ${err.message}` : ''}`,
            err
          )
        }
      }
    }
    fetchCtrData()
  }, [error, mrn, t, userId])

  if (ctrUserData === undefined) return null
  if (ctrUserData.length === 0) return t('ctr.noCtrsFound', 'No CTRs found')

  const renderCtrs = () => {
    return (
      <DmcResponsiveTable
        id="userCtrTable"
        atSizeColumns={{
          default: {
            labels: [
              t('ctrId', 'CTR ID'),
              t('ordinances.ordinance', 'Ordinance'),
              t('date', 'Date'),
              t('temple.label', 'Temple'),
              t('status.label', 'Status'),
            ],
          },
          md: {
            labels: [
              t('ordinances.headers.ordinanceAndCTR', 'Ordinance & CTR'),
              t('ordinances.headers.dateAndTemple', 'Date & Temple'),
              t('status.label', 'Status'),
            ],
            combinationRules: [['column1', 'column2'], ['column3', 'column4'], ['column5']],
          },
          xl: {
            labels: [
              t('ctrId', 'CTR ID'),
              t('ordinances.ordinance', 'Ordinance'),
              t('date', 'Date'),
              t('temple.label', 'Temple'),
              t('status.label', 'Status'),
            ],
          },
        }}
        totalColumnCount={5}
        separator={<br />}
      >
        {ctrUserData.map((ctr) => (
          <ResponsiveTableRow
            key={ctr.ctr
              .concat('_')
              .concat(ctr.ctr ? ctr.ordinance.substr(1, 3) : ctr.ctr)
              .concat('_')
              .concat(ctr.performed)}
            column1={
              ctr.ctr ? (
                <>
                  <CtrLink ctrId={ctr.ctr} newTab /> ({getNumericalIdFromId(ctr.ctr)})
                </>
              ) : null
            }
            column2={t(`ctr.ordinanceType.${ctr.ordinance}`, ctr.ordinance)}
            column3={ctr.performed}
            column4={ctr.temple}
            column5={t(`ctr.ordinanceStatus.${ctr.status}`, ctr.status)}
          />
        ))}
      </DmcResponsiveTable>
    )
  }

  const knownUser = mrn != null || userId != null
  const ctrsExist = ctrUserData && ctrUserData.length > 0

  return (
    <>
      {!knownUser && <h3>{t('error.unableToFindUser', 'Unable to find user.')}</h3>}
      {knownUser && ctrsExist && renderCtrs()}
      {knownUser && ctrUserData && !ctrsExist && t('error.notFound', { resource: 'CTR' })}
    </>
  )
}

export default UserCtr
